import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  VOICE_CONTROL_STATUS,
  createVoiceControlController,
  getVoiceRecognitionLanguage,
  isVoiceRecognitionSupported,
} from "../../services/voiceControl";
import { REPLAY_VOICE_HELP, parseReplayVoiceCommand } from "../../services/replayVoiceCommands";
import "./replayVoiceControl.css";

// «Activar control de voz» de la repeticion. Usa el mismo motor de voz que la
// partida (VoiceControlController) con los comandos del replay. Cada comando lo
// ejecuta onCommand con las mismas funciones que los botones del panel.
const ERROR_COPY = {
  unsupported: "Tu navegador no permite control de voz. Prueba con Chrome o Edge.",
  denied: "Permite el micrófono para usar el control de voz.",
  unavailable: "No se encontró un micrófono disponible.",
  error: "El reconocimiento de voz falló. Pulsa el botón para intentarlo otra vez.",
};

export default function ReplayVoiceControl({ active, onCommand }) {
  const [enabled, setEnabled] = useState(false);
  const [status, setStatus] = useState(VOICE_CONTROL_STATUS.DISABLED);
  const [notice, setNotice] = useState("");
  const [lastHeard, setLastHeard] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [hearing, setHearing] = useState("");
  const controllerRef = useRef(null);
  const gotoTimerRef = useRef(null);
  const onCommandRef = useRef(onCommand);
  onCommandRef.current = onCommand;

  const stopVoice = useCallback(() => {
    controllerRef.current?.stop();
    setEnabled(false);
  }, []);

  const handleStatus = useCallback((nextStatus) => {
    setStatus(nextStatus);
    if (nextStatus === VOICE_CONTROL_STATUS.PERMISSION_DENIED) setNotice(ERROR_COPY.denied);
    else if (nextStatus === VOICE_CONTROL_STATUS.UNSUPPORTED) setNotice(ERROR_COPY.unsupported);
    else if (nextStatus === VOICE_CONTROL_STATUS.UNAVAILABLE) setNotice(ERROR_COPY.unavailable);
    else if (nextStatus === VOICE_CONTROL_STATUS.ERROR) setNotice(ERROR_COPY.error);
    if (
      [
        VOICE_CONTROL_STATUS.PERMISSION_DENIED,
        VOICE_CONTROL_STATUS.UNSUPPORTED,
        VOICE_CONTROL_STATUS.UNAVAILABLE,
      ].includes(nextStatus)
    ) {
      setEnabled(false);
    }
  }, []);

  const runCommandNow = useCallback(
    (command) => {
      let result = null;
      try {
        result = onCommandRef.current?.(command);
      } catch (error) {
        console.log(error);
      }
      if (result && typeof result === "object") {
        setLastHeard({ ok: result.ok !== false, text: String(result.text || command.label) });
        return;
      }
      setLastHeard(
        result === false
          ? { ok: false, text: `${command.label}: ahora no se puede` }
          : { ok: true, text: typeof result === "string" && result ? result : command.label }
      );
    },
    []
  );

  const runCommand = useCallback(
    (command) => {
      setHearing("");
      if (command.id === "voice_off") {
        setLastHeard({ ok: true, text: "Control de voz apagado" });
        stopVoice();
        return;
      }
      if (command.id === "voice_help") {
        setHelpOpen(true);
        setLastHeard({ ok: true, text: command.label });
        return;
      }
      // «jugada 1…» llega antes que «jugada 12»: se espera un momento y vale la ultima.
      if (String(command.id).startsWith("goto:")) {
        window.clearTimeout(gotoTimerRef.current);
        gotoTimerRef.current = window.setTimeout(() => {
          gotoTimerRef.current = null;
          runCommandNow(command);
        }, 900);
        setLastHeard({ ok: true, text: `${command.label}…` });
        return;
      }
      runCommandNow(command);
    },
    [runCommandNow, stopVoice]
  );

  const handleTranscript = useCallback((entry) => {
    if (entry?.stage === "interim_unmatched") {
      setHearing(String(entry.transcript || "").trim());
      return;
    }
    if (entry?.stage !== "unmatched") return;
    setHearing("");
    const heard = String(entry.transcript || entry.normalizedTranscript || "").trim();
    if (heard) setLastHeard({ ok: false, text: `No entendí: «${heard}»` });
  }, []);

  // Lo mismo que muestra la partida, para saber si Chrome oye el microfono.
  const handleEngineEvent = useCallback((detail = {}) => {
    const stage = String(detail.stage || "");
    if (stage === "yielded") {
      setHearing("");
      setNotice("Otra pestaña de XIMA empezó a usar el control de voz. Desactívalo y actívalo otra vez para usarlo aquí.");
      return;
    }
    if (stage === "resumed" || stage === "recognition_started") setNotice("");
    if (stage === "aborted_loop") {
      setNotice(
        "Chrome corta la escucha: otra pestaña o programa está usando el reconocimiento de voz. Cierra la partida abierta en otra pestaña o apaga allí el control de voz."
      );
      return;
    }
    if (stage === "speech_started") setHearing("…");
    else if (stage === "no_speech") setHearing("");
    else if (stage === "recognition_error") {
      console.warn("[voz replay] error del reconocimiento:", detail.error);
      if (String(detail.error || "").includes("network")) {
        setNotice("El reconocimiento de voz necesita internet (Chrome usa el servicio de Google).");
      }
    }
  }, []);

  const ensureController = useCallback(() => {
    const options = {
      language: getVoiceRecognitionLanguage("es"),
      parseCommand: parseReplayVoiceCommand,
      onStatusChange: handleStatus,
      onCommand: runCommand,
      onTranscript: handleTranscript,
      onEngineEvent: handleEngineEvent,
    };
    if (!controllerRef.current) controllerRef.current = createVoiceControlController(options);
    else controllerRef.current.updateOptions(options);
    return controllerRef.current;
  }, [handleEngineEvent, handleStatus, handleTranscript, runCommand]);

  const startVoice = useCallback(async () => {
    setNotice("");
    setLastHeard(null);
    if (!isVoiceRecognitionSupported()) {
      setStatus(VOICE_CONTROL_STATUS.UNSUPPORTED);
      setNotice(ERROR_COPY.unsupported);
      return;
    }
    setEnabled(true);
    try {
      const started = await ensureController().start({ requestPermission: true });
      if (!started && !controllerRef.current?.enabled) setEnabled(false);
    } catch (error) {
      console.log(error);
      setEnabled(false);
      setNotice(ERROR_COPY.error);
    }
  }, [ensureController]);

  // Respuesta del tablero a «mueve…» / «incorpora…» (con «Tomar el control»).
  useEffect(() => {
    if (!enabled) return undefined;
    const handleAck = (event) => {
      if (event?.origin && event.origin !== window.location.origin) return;
      const data = event?.data;
      if (!data || typeof data !== "object" || data.type !== "XIMA_VOICE_COMMAND_ACK") return;
      if (data.stage !== "move_feedback") return;
      const message = String(data.message || "").trim();
      if (!message) return;
      setLastHeard({ ok: /^(Movido|Incorporado|Origen)/.test(message), text: message });
    };
    window.addEventListener("message", handleAck);
    return () => window.removeEventListener("message", handleAck);
  }, [enabled]);

  // Apagado al salir de la repeticion (y al desmontar).
  useEffect(() => {
    if (!active) {
      window.clearTimeout(gotoTimerRef.current);
      controllerRef.current?.stop();
      setEnabled(false);
      setHelpOpen(false);
      setLastHeard(null);
      setNotice("");
    }
  }, [active]);

  useEffect(
    () => () => {
      window.clearTimeout(gotoTimerRef.current);
      controllerRef.current?.destroy();
      controllerRef.current = null;
    },
    []
  );

  const listening = enabled && status === VOICE_CONTROL_STATUS.LISTENING;
  const waiting =
    enabled &&
    [VOICE_CONTROL_STATUS.REQUESTING_PERMISSION, VOICE_CONTROL_STATUS.AWAITING_PERMISSION].includes(status);

  return (
    <section className="r7-card r7-voice">
      <button
        type="button"
        className={`r7-voice-btn${enabled ? " is-on" : ""}`}
        onClick={() => (enabled ? stopVoice() : startVoice())}
        aria-pressed={enabled}
      >
        {enabled ? (
          <>
            <span className={`r7-voice-dot${listening ? " is-live" : ""}`} aria-hidden="true" />
            Desactivar control de voz
          </>
        ) : (
          <>🎤 Activar control de voz</>
        )}
      </button>
      {enabled ? (
        <div className="r7-voice-state">
          {waiting ? "Esperando permiso del micrófono…" : listening ? "Escuchando…" : "Activo"}
        </div>
      ) : null}
      {enabled && hearing ? <div className="r7-voice-hearing">Oigo: {hearing}</div> : null}
      {notice ? <div className="r7-voice-notice">{notice}</div> : null}
      {lastHeard ? (
        <div className={`r7-voice-last${lastHeard.ok ? "" : " is-miss"}`}>
          {lastHeard.ok ? "✓ " : ""}
          {lastHeard.text}
        </div>
      ) : null}
      <div className="r7-voice-help-row">
        <span>Di «siguiente», «atrás» o «play»</span>
        <button type="button" onClick={() => setHelpOpen((prev) => !prev)}>
          {helpOpen ? "Ocultar comandos" : "Ver comandos"}
        </button>
      </div>
      {helpOpen ? (
        <ul className="r7-voice-help">
          {REPLAY_VOICE_HELP.map((pair) => (
            <li key={pair[0]}>
              <span>{pair[0]}</span>
              <span>{pair[1]}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
